import type { Data } from '@puckeditor/core'

import type { FooterConfig, HeaderConfig } from '@/types/header-footer-config'

const EDITOR_ONLY_BLOCKS = new Set(['PuckHeaderSection', 'PuckFooterSection'])

export interface BrandingOverridePayload {
  brandHref?: string
  fallbackLogoUrl?: string
  brandName?: string
}

export interface PublishedChromeConfig {
  header?: {
    visible: boolean
    brandingOverride: BrandingOverridePayload
    headerConfig?: HeaderConfig
  }
  footer?: {
    visible: boolean
    brandingOverride: BrandingOverridePayload
    footerConfig?: FooterConfig
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function toBrandingOverridePayload(props: Record<string, unknown>): BrandingOverridePayload {
  return {
    brandHref: typeof props.brandHref === 'string' ? props.brandHref : undefined,
    fallbackLogoUrl: typeof props.fallbackLogoUrl === 'string' ? props.fallbackLogoUrl : undefined,
    brandName: typeof props.brandName === 'string' ? props.brandName : undefined,
  }
}

function extractPublishedChromeFromValue(value: unknown, chrome: PublishedChromeConfig) {
  if (Array.isArray(value)) {
    value.forEach(item => extractPublishedChromeFromValue(item, chrome))
    return
  }

  if (!isRecord(value)) {
    return
  }

  const type = typeof value.type === 'string' ? value.type : null
  const props = isRecord(value.props) ? value.props : null

  if (type === 'PuckHeaderSection' && props) {
    chrome.header = {
      visible: typeof props.visible === 'boolean' ? props.visible : true,
      brandingOverride: toBrandingOverridePayload(props),
      headerConfig: isRecord(props.headerConfig) ? (props.headerConfig as HeaderConfig) : undefined,
    }
  }

  if (type === 'PuckFooterSection' && props) {
    chrome.footer = {
      visible: typeof props.visible === 'boolean' ? props.visible : true,
      brandingOverride: toBrandingOverridePayload(props),
      footerConfig: isRecord(props.footerConfig) ? (props.footerConfig as FooterConfig) : undefined,
    }
  }

  Object.values(value).forEach(childValue => extractPublishedChromeFromValue(childValue, chrome))
}

function stripEditorOnlyValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    let changed = false
    const next = value.flatMap(item => {
      if (isRecord(item) && typeof item.type === 'string' && EDITOR_ONLY_BLOCKS.has(item.type)) {
        changed = true
        return []
      }

      const normalizedItem = stripEditorOnlyValue(item)
      changed ||= normalizedItem !== item
      return [normalizedItem]
    })

    return changed ? next : value
  }

  if (!isRecord(value)) {
    return value
  }

  let changed = false
  const nextEntries = Object.entries(value).map(([key, childValue]) => {
    const normalizedChild = stripEditorOnlyValue(childValue)
    changed ||= normalizedChild !== childValue
    return [key, normalizedChild]
  })

  return changed ? Object.fromEntries(nextEntries) : value
}

export function extractPublishedChromeConfig(data: Data): PublishedChromeConfig {
  const chrome: PublishedChromeConfig = {}
  extractPublishedChromeFromValue(data, chrome)
  return chrome
}

/**
 * Extract header/footer chrome from full Puck data, then strip editor-only blocks
 * for public rendering. Chrome must be read before stripping PuckHeader/Footer sections.
 */
export function preparePublishedPuckData(data: Data): { data: Data; chrome: PublishedChromeConfig } {
  return {
    chrome: extractPublishedChromeConfig(data),
    data: stripEditorOnlyBlocks(data),
  }
}

export function hasRenderablePuckContent(data: Data): boolean {
  const stripped = stripEditorOnlyBlocks(data) as Data & { content?: unknown[] }
  return Array.isArray(stripped.content) && stripped.content.length > 0
}

/**
 * Check if Puck data contains non-FSDS content (NonFsds* or GeneralV1* components).
 * Used to distinguish FSDS-seeded homepage content from non-FSDS Puck content.
 * When the BE seeds the homepage with FSDS components (SectionHeroV3, etc.) for
 * ALL tenants, non-FSDS domains should NOT render that content via PuckRender
 * and should instead fall back to the static NonFsdsLandingPage.
 */
export function hasNonFsdsPuckContent(data: Data): boolean {
  const stripped = stripEditorOnlyBlocks(data) as Data & { content?: unknown[] }
  if (!Array.isArray(stripped.content)) return false
  return stripped.content.some(
    item =>
      isRecord(item) &&
      typeof item.type === 'string' &&
      (item.type.startsWith('NonFsds') || item.type.startsWith('GeneralV1'))
  )
}

/**
 * Remove editor-only blocks from Puck data before public rendering.
 * Prevents duplicate NavBar/Footer when page is inside RootContainer.
 */
export function stripEditorOnlyBlocks(data: Data): Data {
  return stripEditorOnlyValue(data) as Data
}
